import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, message, Button } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import SalesDashboard from "./SalesDashboard";
import SalesDump from "../DataModule/salesDumpData";
import SalesByDate from "./sales-by-date";
import CustomerReports from "./CustomerReports";
import SalesDumpAnalysis from "../AIAnalysis/salesDumpAnalysis";
import DashboardFilters from "./components/salesdashboard/DashboardFilters";
import { getDashboardFilters } from "../../core/api/dashboard";
import { useSelector } from "react-redux";
const { TabPane } = Tabs;

const SalesDashboardTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [forcedTabKey, setForcedTabKey] = useState(null);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [filtersMinimized, setFiltersMinimized] = useState(() => {
    // Get the saved state from localStorage or default to false if not found
    const savedState = localStorage.getItem('dashboardFiltersMinimized');
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Filter states
  const [saleTypeFilters, setSaleTypeFilters] = useState({
    phone: true,
    mail: true,
    web: true,
    unknown: true,
  });

  const [cascaderOptions, setCascaderOptions] = useState({
    Product: [],
    Category: [],
    Segment: [],
    lists: [],
  });

  const [userSelectedFilters, setUserSelectedFilters] = useState({
    brands: [],
    Product: [],
    Category: [],
    Segment: [],
    lists: [],
    saleTypes: [],
    dateRange: null,
  });
  
  // We need loading state to pass to the filter component
  const [loading] = useState(false);

  // Define tab paths mapping using useMemo to prevent recreation on each render
  const tabKeyToPath = useMemo(() => ({
    1: "sales-dashboard",
    2: "customer-reports",
    3: "sales-by-date",
    4: "sales-dump-analysis",
    5: "sales-dump-data"
  }), []);
  
  // Fetch filter options
  const fetchFilters = async () => {
    try {
      const response = await getDashboardFilters();
      if (response.data) {
        setCascaderOptions({
          brands: response.data.brands || [],
          Product: response.data.products || [],
          Category: response.data.categories || [],
          Segment: response.data.segments || [],
          lists: response.data.lists || [],
          saleTypes: [
            { label: 'Phone', value: 'phone' },
            { label: 'Mail', value: 'mail' },
            { label: 'Web', value: 'web' },
            { label: 'Unknown', value: 'unknown' }
          ]
        });
      }
    } catch (error) {
      message.error('Failed to load filters: ' + (error.response?.data?.error || error.message));
    }
  };
  
  // Update brand filters based on selected client
  // useEffect(() => {
  //   if (selectedClients && selectedClients.length > 0) {
  //     // If 'all' is selected, don't filter by brand
  //     if (selectedClients.includes('all')) {
  //       setUserSelectedFilters(prev => ({
  //         ...prev,
  //         brands: []
  //       }));
  //     } else {
  //       // Use selected clients directly as brand IDs
  //       setUserSelectedFilters(prev => ({
  //         ...prev,
  //         brands: selectedClients
  //       }));
  //     }
  //   }
  // }, [selectedClients]);
  
  // Fetch filters on component mount
  useEffect(() => {
    fetchFilters();
  }, []);
  
  // Effect to sync localStorage with state changes (in case it changes elsewhere)
  useEffect(() => {
    localStorage.setItem('dashboardFiltersMinimized', JSON.stringify(filtersMinimized));
  }, [filtersMinimized]);

  // Create reverse mapping from path to key
  const pathToTabKey = useMemo(() => {
    return Object.entries(tabKeyToPath).reduce(
      (acc, [key, path]) => {
        acc[path] = key;
        return acc;
      },
      {}
    );
  }, [tabKeyToPath]);

  // Get current path from URL
  const currentPath = location.pathname.split("/").pop();
  
  // Determine active tab key based on current path
  const computedKey = pathToTabKey[currentPath] || "1";
  const activeTabKey = forcedTabKey || computedKey;

  // Set default tab on initial load if not on a specific tab path
  useEffect(() => {
    if (!Object.values(tabKeyToPath).includes(currentPath)) {
      navigate(`/sales-dashboard`, { replace: true });
    }
  }, [currentPath, navigate, tabKeyToPath]);

  // Handle tab change
  const handleTabChange = (key) => {
    setForcedTabKey(null);
    navigate(`/${tabKeyToPath[key]}`);
  };

  // Show filters only for Sales Dashboard and Customer Reports tabs (keys "1" and "2")
  const shouldShowFilters = activeTabKey === "1" || activeTabKey === "2";

  return (
    <div className="page-wrapper" style={{ paddingLeft: "10px" }}>
      <div className="">
        {/* Fixed Filter Sidebar - Only shown for specific tabs */}
        {shouldShowFilters && (
          <div className={`dashboard-sidebar ${filtersMinimized ? 'minimized' : ''}`} style={{ 
            transition: 'width 0.3s ease-in-out',
            width: filtersMinimized ? '50px' : '280px',
            overflow: 'hidden'
          }}>
            <div className="" style={{ position: 'relative' }}>
              <Button 
                type="text"
                icon={filtersMinimized ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => {
                  const newState = !filtersMinimized;
                  setFiltersMinimized(newState);
                  // Save the state to localStorage
                  localStorage.setItem('dashboardFiltersMinimized', JSON.stringify(newState));
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  zIndex: 10
                }}
                size="small"
                title={filtersMinimized ? "Expand filters" : "Collapse filters"}
              />
              <div style={{ opacity: filtersMinimized ? 0 : 1, transition: 'opacity 0.3s ease-in-out' }}>
                <DashboardFilters
                  setSaleTypeFilters={setSaleTypeFilters}
                  cascaderOptions={cascaderOptions}
                  setCascaderOptions={setCascaderOptions}
                  userSelectedFilters={userSelectedFilters}
                  setUserSelectedFilters={setUserSelectedFilters}
                  loading={loading}
                  onClose={() => console.log('Filter applied')}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Main Content with Tabs - Full width when filters are hidden */}
        <div className={`${shouldShowFilters ? (filtersMinimized ? 'dashboard-main-expanded' : 'dashboard-main') : 'dashboard-main-full'} px-4`} style={{
          transition: 'margin-left 0.3s ease-in-out',
          marginLeft: shouldShowFilters && filtersMinimized ? '50px' : shouldShowFilters ? '280px' : '0px'
        }}>
          <Tabs
            activeKey={activeTabKey}
            onChange={handleTabChange}
            className=""
            type="card"
            size="small"
            tabBarStyle={{ marginBottom: "5px" }}
          >
            <TabPane tab="Sales Dashboard" key="1">
              <SalesDashboard 
                saleTypeFilters={saleTypeFilters}
                userSelectedFilters={userSelectedFilters}
              />
            </TabPane>
            
            <TabPane tab="Customer Reports" key="2">
              <CustomerReports 
                saleTypeFilters={saleTypeFilters}
                userSelectedFilters={userSelectedFilters}
              />
            </TabPane>
            <TabPane tab="Sales By Date" key="3">
              <SalesByDate />
            </TabPane>
            <TabPane tab="Sales Dump AI Analysis" key="4">
              <SalesDumpAnalysis />
            </TabPane>
            <TabPane tab="Sales Dump Data" key="5">
              <SalesDump />
            </TabPane>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboardTab;
